// -----------------------
// Utility validation functions
// -----------------------
function assert(assertion: boolean, msg: string) {
    if (!assertion) throw new Error(msg);
}

function isPort(val: number) { assert(Number.isInteger(val) && val > 0 && val < 65536, 'must be a valid port'); }
function isFloat(val: number) { assert(Number.isFinite(val), 'must be a number'); }
function isNat(val: number) { assert(Number.isInteger(val) && val >= 0, 'must be a natural number'); }
function isInt(val: number) { assert(Number.isInteger(val), 'must be an integer'); }
function isUrl(val: string) { try { new URL(val, window.location.origin); } catch { throw new Error('must be a valid URL'); } }
function isString(val: any) { assert(typeof val === 'string', 'must be a string'); }
function isBoolean(val: any) { assert(typeof val === 'boolean', 'must be a boolean'); }
function isArray(val: any) { assert(Array.isArray(val), 'must be an array'); }
function isObject(val: any) { assert(typeof val === 'object' && val !== null && !Array.isArray(val), 'must be an object'); }
function isOneOf(val: any, arr: any[]) { assert(arr.includes(val), `must be one of: ${arr.join(', ')}`); }
function isInRange(val: number, min: number, max: number) { assert(val >= min && val <= max, `must be in range [${min}, ${max}]`); }

// -----------------------
// Default configuration
// -----------------------
const defaultConfig = {
    loginEnabled: false,
    developmentPort: 3443,
    productionPort: 443,
    serverHostname: '',
    supportedBrowsers: {
        windows: { 'internet explorer': '>12', 'microsoft edge': '>18' },
        'microsoft edge': '>18',
        safari: '>12',
        firefox: '>=60',
        chrome: '>=74',
        chromium: '>=74',
        opera: '>=62',
        'samsung internet for android': '>=11.1.1.52'
    },
    networkPriorities: { audio: 'high', mainVideo: 'high', additionalVideos: 'medium', screenShare: 'medium' },
    aspectRatio: 1.777,
    aspectRatios: [{ value: 1.333, label: '4 : 3' }, { value: 1.777, label: '16 : 9' }],
    resolution: 'medium',
    frameRate: 15,
    screenResolution: 'veryhigh',
    screenSharingFrameRate: 15,
    simulcast: true,
    simulcastSharing: false,
    simulcastProfiles: {
        3840: [
            { scaleResolutionDownBy: 12, maxBitRate: 150000 },
            { scaleResolutionDownBy: 6, maxBitRate: 500000 },
            { scaleResolutionDownBy: 1, maxBitRate: 10000000 }
        ],
        1920: [
            { scaleResolutionDownBy: 6, maxBitRate: 150000 },
            { scaleResolutionDownBy: 3, maxBitRate: 500000 },
            { scaleResolutionDownBy: 1, maxBitRate: 3500000 }
        ],
        1280: [
            { scaleResolutionDownBy: 4, maxBitRate: 150000 },
            { scaleResolutionDownBy: 2, maxBitRate: 500000 },
            { scaleResolutionDownBy: 1, maxBitRate: 1200000 }
        ],
        640: [
            { scaleResolutionDownBy: 2, maxBitRate: 150000 },
            { scaleResolutionDownBy: 1, maxBitRate: 500000 }
        ],
        320: [{ scaleResolutionDownBy: 1, maxBitRate: 150000 }]
    },
    adaptiveScalingFactor: 0.75,
    localRecordingEnabled: false,
    audioOutputSupportedBrowsers: ['chrome', 'opera'],
    requestTimeout: 20000,
    requestRetries: 3,
    transportOptions: { tcp: true },
    autoGainControl: true,
    echoCancellation: true,
    noiseSuppression: true,
    aiNoiseCancellation: true,
    aiNoiseCancellationLevel: 50,
    voiceActivatedUnmute: false,
    noiseThreshold: -60,
    sampleRate: 48000,
    channelCount: 1,
    sampleSize: 16,
    opusStereo: false,
    opusDtx: true,
    opusFec: true,
    opusPtime: 20,
    opusMaxPlaybackRate: 48000,
    audioPreset: 'conference',
    audioPresets: {
        conference: {
            name: 'Conference audio',
            autoGainControl: true,
            echoCancellation: true,
            noiseSuppression: true,
            voiceActivatedUnmute: false,
            noiseThreshold: -60,
            sampleRate: 48000,
            channelCount: 1,
            sampleSize: 16,
            opusStereo: false,
            opusDtx: true,
            opusFec: true,
            opusPtime: 20,
            opusMaxPlaybackRate: 48000
        },
        hifi: {
            name: 'HiFi streaming',
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false,
            voiceActivatedUnmute: false,
            noiseThreshold: -60,
            sampleRate: 48000,
            channelCount: 2,
            sampleSize: 16,
            opusStereo: true,
            opusDtx: false,
            opusFec: true,
            opusPtime: 60,
            opusMaxPlaybackRate: 48000
        }
    },
    autoMuteThreshold: 4,
    background: 'images/background.jpeg',
    defaultLayout: 'democratic',
    buttonControlBar: false,
    drawerOverlayed: true,
    notificationPosition: 'right',
    notificationSounds: {
        chatMessage: { play: '/sounds/notify-chat.mp3' },
        raisedHand: { play: '/sounds/notify-hand.mp3' },
        default: { delay: 5000, play: '/sounds/notify.mp3' }
    },
    hideTimeout: 3000,
    lastN: 4,
    mobileLastN: 1,
    maxLastN: 5,
    lockLastN: false,
    logo: 'images/logo.png',
    smalllogo: 'images/konx.png',
    sessionlogo: 'images/konx.png',
    title: 'konx',
    supportUrl: 'https://support.example.com',
    privacyUrl: 'privacy/privacy.html',
    theme: {
        palette: { primary: { main: '#313131' } },
        typography: { useNextVariants: true }
    }
};

// -----------------------
// Merge runtime window config
// -----------------------
let config: any = { ...defaultConfig };
if (typeof window !== 'undefined' && (window as any).config) {
    config = { ...config, ...(window as any).config };
}

// -----------------------
// Validation (development only)
// -----------------------
let configError = '';
if (process.env.NODE_ENV !== 'production') {
    try {
        isBoolean(config.loginEnabled);
        isPort(config.developmentPort);
        isPort(config.productionPort);
        isString(config.serverHostname);
        isObject(config.supportedBrowsers);
        isObject(config.networkPriorities);
        isFloat(config.aspectRatio);
        isArray(config.aspectRatios);
        isOneOf(config.resolution, ['low','medium','high','veryhigh','ultra']);
        isNat(config.frameRate);
        isOneOf(config.screenResolution, ['low','medium','high','veryhigh','ultra']);
        isNat(config.screenSharingFrameRate);
        isBoolean(config.simulcast);
        isBoolean(config.simulcastSharing);
        isObject(config.simulcastProfiles);
        isInRange(config.adaptiveScalingFactor, 0.5, 1.0);
        isBoolean(config.localRecordingEnabled);
        isArray(config.audioOutputSupportedBrowsers);
        isNat(config.requestTimeout);
        isNat(config.requestRetries);
        isObject(config.transportOptions);
        isBoolean(config.autoGainControl);
        isBoolean(config.echoCancellation);
        isBoolean(config.noiseSuppression);
        isBoolean(config.aiNoiseCancellation);
        isInRange(config.aiNoiseCancellationLevel, 0, 100);
        isBoolean(config.voiceActivatedUnmute);
        isInt(config.noiseThreshold);
        isOneOf(config.sampleRate, [8000,16000,24000,44100,48000]);
        isOneOf(config.channelCount, [1,2]);
        isOneOf(config.sampleSize, [8,16,24,32]);
        isBoolean(config.opusStereo);
        isBoolean(config.opusDtx);
        isBoolean(config.opusFec);
        isOneOf(config.opusPtime, [3,5,10,20,30,40,50,60]);
        isOneOf(config.opusMaxPlaybackRate,[8000,16000,24000,44100,48000]);
        isString(config.audioPreset);
        isObject(config.audioPresets);
        isNat(config.autoMuteThreshold);
        isString(config.background);
        isOneOf(config.defaultLayout,['democratic','filmstrip']);
        isBoolean(config.buttonControlBar);
        isBoolean(config.drawerOverlayed);
        isOneOf(config.notificationPosition,['left','right']);
        isObject(config.notificationSounds);
        isInt(config.hideTimeout);
        isNat(config.lastN);
        isNat(config.mobileLastN);
        isNat(config.maxLastN);
        isBoolean(config.lockLastN);
        isString(config.logo);
        isString(config.smalllogo);
        isString(config.sessionlogo);
        isString(config.title);
        isString(config.supportUrl);
        isString(config.privacyUrl);
        isObject(config.theme);
    } catch (err: any) {
        configError = err.message;
    }
}

// -----------------------
// formatDocs for documentation
// -----------------------
function formatDocs(obj: any = defaultConfig, prefix: string | null = null) {
    const docs: Record<string, any> = {};

    function recurse(currObj: any, currPath: string | null) {
        for (const key of Object.keys(currObj)) {
            const fullKey = currPath ? `${currPath}.${key}` : key;
            const value = currObj[key];
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                recurse(value, fullKey);
            } else {
                docs[fullKey] = {
                    doc: '',
                    format: JSON.stringify(typeof value),
                    default: JSON.stringify(value)
                };
            }
        }
    }

    recurse(obj, prefix);
    return docs;
}

// -----------------------
// Constants
// -----------------------
const PORTAL = 'BIZ';
// const PORTAL = 'WORLD';

// const API_URL = 'https://api.konx.biz/';
// const API_URL = 'https://api.konx.world/';
// const API_URL = 'https://devapi.konx.club/';
// const API_URL = 'https://devapi.konx.world/';
// const API_URL = 'http://localhost:4040/';
// Relative path so it always resolves against whatever origin the page is
// loaded from (live.konx.biz, or the meet-2 tunnel during testing) - nginx
// proxies /api/ to meet-1 on every node.
const API_URL = '/api/';

// -----------------------
// Exports
// -----------------------
const configSchema = defaultConfig; // For compatibility with old exports
export { configSchema, config, configError, formatDocs, PORTAL, API_URL };
